import React, { useEffect } from 'react';
import { X, ExternalLink, CheckCircle, Tag, Layers, ArrowRight } from 'lucide-react';
import { ProjectItem } from '../../types';

interface ProjectModalProps {
  project: ProjectItem | null;
  onClose: () => void;
  onConsult: () => void;
}

export const ProjectModal: React.FC<ProjectModalProps> = ({ project, onClose, onConsult }) => {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && project) onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [project, onClose]);

  if (!project) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#050A13]/85 backdrop-blur-md animate-fade-in" role="dialog" aria-modal="true">
      <div className="relative w-full max-w-3xl bg-[#071426] border border-[#1b2f4a] rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
        {/* Modal Header */}
        <div className="px-6 py-4 border-b border-[#1b2f4a] flex items-center justify-between bg-[#0B2342]/40">
          <div>
            <span className="text-xs uppercase tracking-wider text-[#159DE8] font-semibold">{project.category}</span>
            <h3 className="text-xl font-bold text-white">{project.title}</h3>
          </div>
          <button 
            onClick={onClose}
            className="p-2 text-[#8FA0B8] hover:text-white hover:bg-white/10 rounded-full transition-colors"
            aria-label="Close project modal"
          >
            <X size={20} />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 overflow-y-auto space-y-6">
          <div className="rounded-xl overflow-hidden border border-[#1b2f4a] bg-[#050A13] aspect-[16/9] max-h-80 w-full relative">
            <img 
              src={project.image} 
              alt={project.title} 
              className="w-full h-full object-cover object-center" 
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#071426]/60 via-transparent to-transparent pointer-events-none" />
          </div>

          <div>
            <h4 className="text-base font-semibold text-white mb-2">Project Overview</h4>
            <p className="text-[#D5DFEC] text-sm leading-relaxed">{project.fullDetails || project.summary}</p>
          </div>

          {project.clientType && (
            <div className="flex items-center gap-2 text-xs text-[#8FA0B8]">
              <Layers size={14} className="text-[#FF851B]" />
              <span>Target Domain: <strong className="text-white">{project.clientType}</strong></span>
            </div>
          )}

          {project.deliverables && (
            <div>
              <h4 className="text-sm font-semibold text-white mb-2">Key Deliverables</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {project.deliverables.map((item, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs text-[#D5DFEC] p-2 bg-[#0B2342]/30 rounded-lg border border-[#1b2f4a]">
                    <CheckCircle size={14} className="text-[#00C853] shrink-0" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {project.results && (
            <div>
              <h4 className="text-sm font-semibold text-white mb-2">Measurable Impact</h4>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {project.results.map((res, i) => (
                  <div key={i} className="p-3 bg-[#0c1a2d] border border-[#159DE8]/30 rounded-xl text-center">
                    <span className="text-xs text-[#159DE8] font-bold block">{res}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <h4 className="text-xs uppercase tracking-wider text-[#8FA0B8] mb-2 font-semibold">Technologies Utilized</h4>
            <div className="flex flex-wrap gap-2">
              {project.tags.map((t, idx) => (
                <span key={idx} className="inline-flex items-center gap-1 px-3 py-1 bg-[#050A13] border border-[#1b2f4a] rounded-full text-xs text-[#D5DFEC]">
                  <Tag size={10} className="text-[#159DE8]" />
                  {t}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="px-6 py-4 border-t border-[#1b2f4a] bg-[#050A13] flex flex-col sm:flex-row items-center justify-between gap-3">
          <span className="text-xs text-[#8FA0B8]">Interested in architecting a similar solution?</span>
          <div className="flex items-center gap-3 w-full sm:w-auto">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-full border border-[#1b2f4a] text-xs text-[#D5DFEC] hover:text-white hover:bg-white/5 transition-all w-full sm:w-auto text-center"
            >
              Close
            </button>
            <button
              onClick={() => {
                onClose();
                onConsult();
              }}
              className="px-5 py-2 rounded-full bg-gradient-to-r from-[#FF5A36] to-[#FF851B] text-white text-xs font-semibold hover:shadow-glow-orange transition-all flex items-center justify-center gap-1.5 w-full sm:w-auto"
            >
              <span>Discuss Your Requirements</span>
              <ArrowRight size={14} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
